import { useState, useEffect, useMemo, useCallback } from 'react';
import { getFlipgiveToken } from 'common/api/crm/affiliates/service';

export const useGetFlipgiveToken = (): {
    token: string;
    isLoading: boolean;
    errorStatus: number | null;
    refetch: () => void;
    settledAttempt: number;
} => {
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorStatus, setErrorStatus] = useState<number | null>(null);
    const [attempt, setAttempt] = useState(0);
    // Last request (by attempt) that finished, so callers can await a refetch without isLoading edges.
    const [settledAttempt, setSettledAttempt] = useState(-1);

    const refetch = useCallback(() => setAttempt((current) => current + 1), []);

    useEffect(() => {
        // Only the initial load shows loading; background revalidations don't.
        if (!token) {
            setIsLoading(true);
        }
        setErrorStatus(null);
        getFlipgiveToken()
            .then((result) => {
                if (result.data?.token) {
                    setToken(result.data.token);
                } else {
                    const status = result.response?.status ?? null;
                    setErrorStatus(status);
                    // A 401 invalidates any previously issued token.
                    if (status === 401) {
                        setToken('');
                    }
                }
            })
            .catch((_) => {})
            .finally(() => {
                setIsLoading(false);
                setSettledAttempt(attempt);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    return useMemo(
        () => ({ token, isLoading, errorStatus, refetch, settledAttempt }),
        [token, isLoading, errorStatus, refetch, settledAttempt]
    );
};

export const useConfigureUrlWithAffiliateToken = (
    pattern: string,
    url?: string,
    queryParams?: string
): [string, boolean, string, number | null, () => void, number] => {
    const { token, isLoading, errorStatus, refetch, settledAttempt } = useGetFlipgiveToken();

    const activationUrl = token && url ? `${url?.replace(pattern, token)}${queryParams ?? ''}` : '';
    return [activationUrl, isLoading, token, errorStatus, refetch, settledAttempt];
};
